import React from "react";
import type { ReportedUserApi } from "../ReportedUsersPage";
import { getInitials } from "../ReportedUsersPage";

interface ReportedUserCardProps {
  user: ReportedUserApi;
  onUserClick: (user: ReportedUserApi) => void;
  onActionClick: (user: ReportedUserApi, e?: React.MouseEvent) => void;
}

const ReportedUserCard: React.FC<ReportedUserCardProps> = ({ user, onUserClick, onActionClick }) => {
  const { userInfo, complaintsCount, lastComplaintDate } = user;
  const isSuspended = userInfo.status !== "AC";

  // Formatear la fecha de la última denuncia
  const formattedDate = new Date(lastComplaintDate).toLocaleDateString('es-ES', {
    day: '2-digit', month: 'short', year: 'numeric'
  });

  return (
    <div
      onClick={() => onUserClick(user)}
      className="bg-white border border-red-200 hover:border-red-400 rounded-xl p-[18px] cursor-pointer transition-all duration-200 relative flex flex-col h-full group hover:-translate-y-0.5 hover:shadow-lg shadow-sm"
    >
      {/* Badge Contador de Denuncias */}
      <div
        className="absolute top-3 right-3 flex items-center bg-red-100 text-red-700 border border-red-200 py-0.5 px-2 rounded-md shadow-sm cursor-default group/badge transition-all duration-300"
        title={`${complaintsCount} ${complaintsCount === 1 ? 'Denuncia' : 'Denuncias'}`}
      >
        <span className="text-red-500 mr-1 shrink-0 leading-none">⚠️</span>
        <span className="text-[11px] font-bold shrink-0">{complaintsCount}</span>

        {/* Texto oculto que se revela en hover */}
        <span className="text-[11px] font-bold overflow-hidden whitespace-nowrap opacity-0 max-w-0 group-hover/badge:max-w-[80px] group-hover/badge:opacity-100 group-hover/badge:ml-1 transition-all duration-300">
          {complaintsCount === 1 ? 'Denuncia' : 'Denuncias'}
        </span>
      </div>

      {/* Info del Usuario */}
      <div className="flex items-start gap-3 mt-1">
        <div className="w-12 h-12 rounded-full shrink-0 flex items-center justify-center text-[17px] font-bold text-white relative overflow-hidden bg-gradient-to-br from-gray-600 to-gray-800">
          {userInfo.photoLink ? (
            <img src={userInfo.photoLink} alt={userInfo.name} className="w-full h-full object-cover" />
          ) : (
            getInitials(userInfo.name, userInfo.lastName)
          )}
          <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white z-10 ${isSuspended ? "bg-red-500" : "bg-emerald-500"
            }`}></div>
        </div>

        <div className="flex-1 min-w-0 pr-6">
          <div className="text-sm font-bold text-gray-900 leading-tight break-words line-clamp-2">
            {userInfo.name} {userInfo.lastName}
          </div>
          <div className="text-[11px] text-gray-500 leading-tight mt-0.5 break-words line-clamp-2">
            {userInfo.email || "Sin correo visible"}
          </div>

          <div className="flex gap-1.5 mt-1.5 flex-wrap">
            {userInfo.isSuperHost && (
              <span className="whitespace-nowrap text-[9px] font-bold px-[7px] py-[2px] rounded-md tracking-wider border bg-amber-50 text-amber-600 border-amber-200">
                ⭐ Super Anfitrión
              </span>
            )}
            <span className={`text-[9px] font-bold px-[7px] py-[2px] rounded-md uppercase tracking-wider border ${!isSuspended ? "bg-emerald-50 text-emerald-600 border-emerald-100" : "bg-red-50 text-red-600 border-red-100"}`}>
              {isSuspended ? "Suspendido" : "Activo"}
            </span>
          </div>
        </div>
      </div>

      {/* Acciones */}
      <div className="mt-auto flex flex-col">
        <div className="h-px bg-gray-100 mt-4 mb-3"></div>

        <div className="flex justify-between items-center text-center px-1 mb-2.5">
          <div className="text-left">
            <div className="text-[10px] font-semibold text-gray-400 uppercase">Última denuncia</div>
            <div className="text-xs font-bold text-gray-700 mt-0.5">{formattedDate}</div>
          </div>
        </div>

        <div className="flex gap-1.5">
          <button
            className="flex-1 py-1.5 border border-red-200 bg-red-50 text-red-700 rounded-md text-[11px] font-semibold transition-colors hover:bg-red-100 shadow-sm"
            onClick={(e) => onActionClick(user, e)}
          >
            Ver reportes
          </button>
        </div>
      </div>

    </div>
  );
};

export default ReportedUserCard;